import axios, {
	AxiosInstance,
	AxiosResponse,
	InternalAxiosRequestConfig,
	ResponseType,
} from "axios";

import { Config } from "@/config/Config";

export interface ApiOptions {
	data?: unknown;
	params?: unknown;
	skipAuth?: boolean;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
	__isRetry?: boolean;
}

export interface AuthHandlers {
	// Session could not be recovered: clear storage and send to /login.
	onUnauthorized: () => void;
	// Persists a freshly rotated pair.
	onTokensRenewed: (tokens: TokenPair) => void;
}

export interface TokenPair {
	access_token: string;
	refresh_token?: string;
	expires_in?: number;
}

// Opts into short-lived access tokens; without it the server keeps issuing the
// legacy 30-day ones.
const CLIENT_AUTH_HEADER = "X-Client-Auth";
const CLIENT_AUTH_VERSION = "v2";

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";

// Framework-free HTTP layer. On 401 it exchanges the refresh token for a new
// pair (one exchange at a time, since the server rotates on use) and replays
// the original request once.
export default class API {
	axios: AxiosInstance = axios.create();

	private authHandlers: AuthHandlers | null = null;
	private refreshPromise: Promise<string | null> | null = null;

	constructor() {
		this.axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const url: string | undefined =
					error.response?.request?.responseURL || error.config?.url;
				if (
					!error.response ||
					error.response.status !== 401 ||
					url?.endsWith("/auth/login") ||
					url?.endsWith("/auth/refresh")
				) {
					return Promise.reject(error);
				}

				const config = error.config as RetriableConfig | undefined;
				if (!config || config.__isRetry) {
					return Promise.reject(error);
				}

				const token = await this.refreshAccessToken();
				if (!token) {
					return Promise.reject(error);
				}

				config.__isRetry = true;
				config.headers.Authorization = `Bearer ${token}`;
				return this.axios.request(config);
			}
		);
	}

	registerAuthHandlers(handlers: AuthHandlers) {
		this.authHandlers = handlers;
	}

	getToken(): string | null {
		return window.localStorage.getItem(TOKEN_KEY);
	}

	getRefreshToken(): string | null {
		return window.localStorage.getItem(REFRESH_TOKEN_KEY);
	}

	storeTokens(tokens: TokenPair) {
		window.localStorage.setItem(TOKEN_KEY, tokens.access_token);
		if (tokens.refresh_token) {
			window.localStorage.setItem(
				REFRESH_TOKEN_KEY,
				tokens.refresh_token
			);
		}
	}

	clearTokens() {
		window.localStorage.removeItem(TOKEN_KEY);
		window.localStorage.removeItem(REFRESH_TOKEN_KEY);
	}

	// Bare axios: going through this.axios would re-enter the 401 interceptor.
	async refreshSession(refreshToken: string): Promise<TokenPair> {
		const response = await axios.request({
			method: "POST",
			url: Config.backend_uri + "/auth/refresh",
			data: { refresh_token: refreshToken },
			headers: { [CLIENT_AUTH_HEADER]: CLIENT_AUTH_VERSION },
		});
		return response.data;
	}

	async revokeSession(refreshToken: string): Promise<void> {
		await axios.request({
			method: "POST",
			url: Config.backend_uri + "/auth/logout",
			data: { refresh_token: refreshToken },
		});
	}

	// At most one exchange in flight: the server rotates the refresh token on
	// use, so a second parallel call would present a spent token and look like
	// a replay. Returns null when no fresh token could be obtained.
	private refreshAccessToken(): Promise<string | null> {
		this.refreshPromise ??= (async () => {
			const refreshToken = this.getRefreshToken();
			if (!refreshToken) {
				// Nothing to exchange: the 401 is terminal.
				this.authHandlers?.onUnauthorized();
				return null;
			}
			try {
				const tokens = await this.refreshSession(refreshToken);
				this.storeTokens(tokens);
				this.authHandlers?.onTokensRenewed(tokens);
				return tokens.access_token;
			} catch (error) {
				// Only a refusal ends the session. A network failure or a 5xx
				// says nothing about whether the session is still valid.
				if (
					axios.isAxiosError(error) &&
					error.response?.status === 401
				) {
					this.authHandlers?.onUnauthorized();
				}
				return null;
			}
		})().finally(() => {
			this.refreshPromise = null;
		});
		return this.refreshPromise;
	}

	public async processRequest(
		method: string,
		url: string,
		options: ApiOptions | null = null,
		token: string | null = null,
		contentType: ResponseType = "json"
	): Promise<AxiosResponse> {
		if (!options) {
			options = {
				data: {},
				params: {},
				skipAuth: false,
			};
		}
		if (!token && !options.skipAuth) {
			token = this.getToken();
			if (!token) {
				throw new Error("No token provided");
			}
		}
		return this.axios.request({
			method,
			url: Config.backend_uri + url,
			data: options.data,
			headers: {
				Authorization: "Bearer " + token,
				[CLIENT_AUTH_HEADER]: CLIENT_AUTH_VERSION,
			},
			params: options.params,
			responseType: contentType,
		});
	}
}
